import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {Action, FormDesignerAction, ProcessTaskSchema} from 'forms/form-designer/Constants';
import Label from 'ui-library/bs/Label';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export interface FormDesignerActionSelectorProps {
  formAction: FormDesignerAction;
  actions: Array<Action>;
  schemas: Array<ProcessTaskSchema>;
  onSetAction: (action: Action) => void;
  onSetSchema: (schema: ProcessTaskSchema) => void;
  /** Leaving this blank will not render the button to delete  */
  onRemoveAction?: (actionKey: string) => void;
  /** Prevent changing the action value */
  disableAction?: boolean;
  /** Prevent changing the schema value */
  disableSchema?: boolean;
}

export default function FormDesignerActionSelector({
  formAction,
  actions,
  schemas,
  onSetAction,
  onSetSchema,
  onRemoveAction,
  disableAction,
  disableSchema,
}: FormDesignerActionSelectorProps): JSX.Element {
  return (
    <div className={classNames('u-spacing-bottom', css(styles.grid))}>
      <Label>Action</Label>
      <SelectDropdown
        displayName={formAction.selectedAction?.name ?? ''}
        disabled={disableAction}
        menu={
          <Menu width={Menu.WIDTHS.FULL}>
            {actions.map((action) => (
              <Option
                key={action.identifier}
                value={action}
                onSelect={(act: Action) => {
                  onSetAction(act);
                }}
              >
                {action.name}
              </Option>
            ))}
          </Menu>
        }
      />
      <Label>Schema</Label>
      <SelectDropdown
        disabled={disableSchema || !formAction.selectedAction}
        displayName={formAction.selectedSchema?.name ?? ''}
        menu={
          <SearchableMenu
            width={Menu.WIDTHS.FULL}
            optionDatas={schemas.map((schema) => ({
              name: schema.name,
              value: schema,
            }))}
            onSelect={onSetSchema}
          />
        }
      />
      <div /> {/* Empty div necessary to move Button onto second row of CSS grid */}
      {onRemoveAction && (
        <Button
          buttonStyle={ButtonStyle.CIRCLE_ICON}
          size={ButtonSize.SMALL}
          icon="delete"
          isDropdown={false}
          iconAltText="Remove action"
          onClick={() => {
            onRemoveAction(formAction.key);
          }}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gridTemplateRows: 'auto auto',
    gridAutoFlow: 'column',
    columnGap: '10px',
  },
});
